import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  CircularProgress,
  Container,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Event as EventIcon,
  DirectionsCar as RoadIcon,
  People as CrowdIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';
import { sanitizeInput } from '../lib/validation';

interface Event {
  id: number;
  show_name: string;
  start_datetime: string;
  end_datetime: string;
  status: 'upcoming' | 'ongoing' | 'over';
}

interface CrowdDensity {
  id: number;
  show_name: string;
  capacity: number;
  attendance_strength: number;
  status: 'low' | 'medium' | 'high';
}

interface RoadCondition {
  id: number;
  name: string;
  status: 'Affected' | 'Not Affected';
}

const statusColors = {
  upcoming: 'info',
  ongoing: 'success',
  over: 'error',
} as const;

const densityColors = {
  low: 'success',
  medium: 'warning',
  high: 'error',
} as const;

const roadStatusColors = {
  'Affected': 'error',
  'Not Affected': 'success',
} as const;

const allowedEventStatuses = ['upcoming', 'ongoing', 'over'];
const allowedRoadStatuses = ['Affected', 'Not Affected'];

export default function EventStatus() {
  const [events, setEvents] = useState<Event[]>([]);
  const [crowdDensities, setCrowdDensities] = useState<CrowdDensity[]>([]);
  const [roadConditions, setRoadConditions] = useState<RoadCondition[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editingRoad, setEditingRoad] = useState<RoadCondition | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [roadUpdateDialogOpen, setRoadUpdateDialogOpen] = useState(false);
  const [view, setView] = useState<'events' | 'roads'>('events');

  useEffect(() => {
    // Initial data fetch
    fetchEvents();
    fetchCrowdDensities();
    fetchRoadConditions();

    // Set up real-time subscriptions
    const eventsSubscription = supabase
      .channel('event_status_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_status' }, fetchEvents)
      .subscribe();

    const crowdDensitySubscription = supabase
      .channel('crowd_density_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crowd_density' }, fetchCrowdDensities)
      .subscribe();

    const roadConditionsSubscription = supabase
      .channel('road_conditions_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'road_conditions' }, fetchRoadConditions)
      .subscribe();

    // Clean up subscriptions on component unmount
    return () => {
      supabase.removeChannel(eventsSubscription);
      supabase.removeChannel(crowdDensitySubscription);
      supabase.removeChannel(roadConditionsSubscription);
    };
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('event_status')
        .select('*')
        .order('start_datetime');

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      setError('Failed to fetch events');
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCrowdDensities = async () => {
    try {
      const { data, error } = await supabase
        .from('crowd_density')
        .select('*')
        .order('show_name');

      if (error) throw error;
      setCrowdDensities(data || []);
    } catch (error) {
      setError('Failed to fetch crowd densities');
      console.error('Error fetching crowd densities:', error);
    }
  };

  const fetchRoadConditions = async () => {
    try {
      const { data, error } = await supabase
        .from('road_conditions')
        .select('*')
        .order('name');

      if (error) throw error;
      setRoadConditions(data || []);
    } catch (error) {
      setError('Failed to fetch road conditions');
      console.error('Error fetching road conditions:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const sanitizedQuery = sanitizeInput(searchQuery);
    if (!sanitizedQuery) {
      setError('Please enter a search query.');
      return;
    }
    setSearchQuery(sanitizedQuery);
    try {
      setLoading(true);
      if (view === 'events') {
        const [eventsResponse, crowdResponse] = await Promise.all([
          supabase
            .from('event_status')
            .select('*')
            .ilike('show_name', `%${searchQuery}%`)
            .order('start_datetime'),
          supabase
            .from('crowd_density')
            .select('*')
            .ilike('show_name', `%${searchQuery}%`)
            .order('show_name'),
        ]);

        if (eventsResponse.error) throw eventsResponse.error;
        if (crowdResponse.error) throw crowdResponse.error;

        setEvents(eventsResponse.data || []);
        setCrowdDensities(crowdResponse.data || []);
      } else {
        const { data, error } = await supabase
          .from('road_conditions')
          .select('*')
          .ilike('name', `%${searchQuery}%`)
          .order('name');

        if (error) throw error;
        setRoadConditions(data || []);
      }
    } catch (error) {
      setError('Failed to search.');
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!editingEvent) return;
    setError(null);
    // Validate status
    if (!allowedEventStatuses.includes(editingEvent.status)) {
      setError('Invalid event status.');
      return;
    }
    try {
      setLoading(true);
      const { error } = await supabase
        .from('event_status')
        .update({ status: editingEvent.status })
        .eq('id', editingEvent.id);

      if (error) throw error;

      setSuccess('Event status updated successfully');
      setUpdateDialogOpen(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      setError('Failed to update event status');
      console.error('Error updating event status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoadStatus = async () => {
    if (!editingRoad) return;
    setError(null);
    // Validate status
    if (!allowedRoadStatuses.includes(editingRoad.status)) {
      setError('Invalid road status.');
      return;
    }
    try {
      setLoading(true);
      const { error } = await supabase
        .from('road_conditions')
        .update({ status: editingRoad.status })
        .eq('id', editingRoad.id);

      if (error) throw error;

      setSuccess('Road status updated successfully');
      setRoadUpdateDialogOpen(false);
      setEditingRoad(null);
      fetchRoadConditions();
    } catch (error) {
      setError('Failed to update road status');
      console.error('Error updating road status:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateDensityPercentage = (strength: number, capacity: number) => {
    return (strength / capacity) * 100;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ 
        fontWeight: 600,
        mb: 4,
        color: '#1a237e',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        <EventIcon sx={{ fontSize: 32 }} />
        Event Status Dashboard
      </Typography>

      <Stack spacing={4} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Card sx={{ 
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: '#1a237e',
                }}>
                  Search & Filter
              </Typography>
              <ToggleButtonGroup
                value={view}
                exclusive
                  onChange={(_e: React.MouseEvent<HTMLElement>, newView: 'events' | 'roads') => newView && setView(newView)}
                  sx={{ 
                    '& .MuiToggleButton-root': {
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                    },
                  }}
              >
                  <ToggleButton value="events">
                    <EventIcon sx={{ mr: 1 }} />
                    Events
                  </ToggleButton>
                  <ToggleButton value="roads">
                    <RoadIcon sx={{ mr: 1 }} />
                    Roads
                  </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <form onSubmit={handleSearch}>
                <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                    label={view === 'events' ? 'Search Events' : 'Search Roads'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: '#1a237e' }} />,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
              />
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ 
                      height: 48,
                      minWidth: 120,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 6px 8px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    Search
              </Button>
                </Stack>
            </form>
            </Stack>
          </CardContent>
        </Card>

      {view === 'events' ? (
          <Card sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}>
              <CardContent>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 600,
                color: '#1a237e',
                mb: 3,
              }}>
                Events & Crowd Status
                </Typography>
              <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                      <TableCell>Show Name</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                        <TableCell>Status</TableCell>
                      <TableCell>Crowd Density</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {events.map((event) => {
                      const crowdDensity = crowdDensities.find(
                        (density) => density.show_name === event.show_name
                      );
                      return (
                        <TableRow key={event.id}>
                          <TableCell>{event.show_name}</TableCell>
                          <TableCell>{formatDateTime(event.start_datetime)}</TableCell>
                          <TableCell>{formatDateTime(event.end_datetime)}</TableCell>
                          <TableCell>
                            <Chip
                              label={event.status}
                              color={statusColors[event.status]}
                              sx={{ 
                                borderRadius: 1,
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {crowdDensity ? (
                              <Box sx={{ minWidth: 200 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Chip
                                    icon={<CrowdIcon />}
                                    label={`${crowdDensity.attendance_strength}/${crowdDensity.capacity}`}
                                    color={densityColors[crowdDensity.status]}
                                    size="small"
                                    sx={{ mr: 1, borderRadius: 1 }}
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    {calculateDensityPercentage(
                                      crowdDensity.attendance_strength,
                                      crowdDensity.capacity
                                    )}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={calculateDensityPercentage(
                                    crowdDensity.attendance_strength,
                                    crowdDensity.capacity
                                  )}
                                  color={densityColors[crowdDensity.status]}
                                  sx={{ 
                                    height: 8,
                                    borderRadius: 4,
                                  }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No data
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
      ) : (
          <Card sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}>
          <CardContent>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 600,
                color: '#1a237e',
                mb: 3,
              }}>
              Road Conditions
            </Typography>
              <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Road Name</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roadConditions.map((road) => (
                    <TableRow key={road.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon color="action" />
                            {road.name}
                          </Box>
                        </TableCell>
                      <TableCell>
                        <Chip
                          label={road.status}
                          color={roadStatusColors[road.status]}
                            sx={{ 
                              borderRadius: 1,
                              fontWeight: 500,
                            }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
      </Stack>

      <Dialog
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          color: '#1a237e',
        }}>
          Update Event Status
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="subtitle1">
              Event: {editingEvent?.show_name}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editingEvent?.status || 'upcoming'}
                label="Status"
                onChange={(e) =>
                  setEditingEvent(
                    editingEvent
                      ? { ...editingEvent, status: e.target.value as Event['status'] }
                      : null
                  )
                }
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.1)',
                  }
                }}
              >
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="over">Over</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setUpdateDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={roadUpdateDialogOpen}
        onClose={() => setRoadUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          color: '#1a237e',
        }}>
          Update Road Status
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="subtitle1">
              Road: {editingRoad?.name}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editingRoad?.status || 'Not Affected'}
                label="Status"
                onChange={(e) =>
                  setEditingRoad(
                    editingRoad
                      ? { ...editingRoad, status: e.target.value as RoadCondition['status'] }
                      : null
                  )
                }
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.1)',
                  }
                }}
              >
                <MenuItem value="Affected">Affected</MenuItem>
                <MenuItem value="Not Affected">Not Affected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setRoadUpdateDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRoadStatus}
            variant="contained"
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? 'error' : 'success'}
          sx={{ 
            width: '100%',
            borderRadius: 2,
          }}
      >
          {error || success}
        </Alert>
      </Snackbar>
    </Container>
  );
} 